import { useState } from 'react';
import { Switch } from '../../../../ui/components/ui/switch';
import iconGlasses from '../../res/icon-glasses.png';
import iconMoney from '../../res/icon-money.png';
import iconPen from '../../res/icon-pen.png';
import { CardResponseDto, FormulaName, RangeValueType } from '../../types/response';
import MetricRow, { MetricFooterRowSkeleton, MetricRowSkeleton, RangeEditState } from '../metric-row/metric-row';

const CARD_ICONS: Record<string, string> = {
  'icon-glasses': iconGlasses,
  'icon-money': iconMoney,
  'icon-pen': iconPen
};

interface MetricCardProps {
  card: CardResponseDto;
  sectionKey: string;
  isEditMode: boolean;
  editedValues: Record<string, string>;
  editedRanges?: Record<string, RangeEditState>;
  onValueChange: (metricPath: string, value: string) => void;
  onRangeChange?: (metricPath: string, rangeState: RangeEditState) => void;
}

const createMetricPath = (sectionKey: string, cardKey: string, metricKey: string) => `${sectionKey}.${cardKey}.${metricKey}`;

const buildDefaultRangeState = (cardMetric: CardResponseDto['metrics'][number]): RangeEditState => ({
  min: {
    type: cardMetric.min?.type ?? RangeValueType.STATIC,
    value: cardMetric.min?.value !== null && cardMetric.min?.value !== undefined ? String(cardMetric.min.value) : '',
    formula: cardMetric.min?.calculationFormula ?? FormulaName.MIN_LEADS_FROM_BUDGET
  },
  max: {
    type: cardMetric.max?.type ?? RangeValueType.STATIC,
    value: cardMetric.max?.value !== null && cardMetric.max?.value !== undefined ? String(cardMetric.max.value) : '',
    formula: cardMetric.max?.calculationFormula ?? FormulaName.MIN_LEADS_FROM_BUDGET
  }
});

const MetricCard = ({ card, sectionKey, isEditMode, editedValues, editedRanges = {}, onValueChange, onRangeChange }: MetricCardProps) => {
  const [showMinMax, setShowMinMax] = useState(false);

  const metricRows = card.metrics.filter((metric) => metric.displayVariant === 'metric');
  const footerRows = card.metrics.filter((metric) => metric.displayVariant === 'footer');

  const renderMetricRow = (metric: CardResponseDto['metrics'][number]) => {
    const metricPath = createMetricPath(sectionKey, card.cardKey, metric.metricKey);
    const rangeState = editedRanges[metricPath] ?? buildDefaultRangeState(metric);

    return (
      <MetricRow
        key={metric.metricKey}
        metric={metric}
        isEditMode={isEditMode}
        editValue={editedValues[metricPath] ?? String(metric.value ?? '')}
        onValueChange={(value) => onValueChange(metricPath, value)}
        showMinMax={metric.displayVariant === 'metric' ? showMinMax : false}
        rangeState={rangeState}
        onRangeChange={onRangeChange ? (nextRangeState) => onRangeChange(metricPath, nextRangeState) : undefined}
      />
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E4E7EC] p-4" style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.08)' }}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {card.icon && CARD_ICONS[card.icon] && <img src={CARD_ICONS[card.icon]} alt="" className="w-5" />}
            <span className="text-base font-bold text-[#12282A]">{card.label}</span>
          </div>
          <Switch checked={showMinMax} onCheckedChange={setShowMinMax} />
        </div>

        <div className="flex flex-col gap-2">{metricRows.map(renderMetricRow)}</div>
      </div>

      {footerRows.length > 0 && <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-[#E4E7EC]">{footerRows.map(renderMetricRow)}</div>}
    </div>
  );
};

export default MetricCard;

export const MetricCardSkeleton = ({ rowCount = 3, rowFooterCount = 1 }: { rowCount?: number; rowFooterCount?: number }) => (
  <div className="bg-white rounded-2xl border border-[#E4E7EC] p-4 animate-pulse" style={{ boxShadow: '0px 4px 12.7px 0px rgba(0, 0, 0, 0.08)' }}>
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 bg-[#E4E7EC] rounded-full" />
          <div className="w-28 h-4 bg-[#E4E7EC] rounded-full" />
        </div>
        <div className="w-8 h-4 bg-[#E4E7EC] rounded-full" />
      </div>
      <div className="flex flex-col gap-2 w-full">
        {Array.from({ length: rowCount }).map((_, i) => (
          <MetricRowSkeleton key={i} />
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-[#E4E7EC]">
        {Array.from({ length: rowFooterCount }).map((_, i) => (
          <MetricFooterRowSkeleton key={i} />
        ))}
      </div>
    </div>
  </div>
);
